import { Sequence, useCurrentFrame } from 'remotion';
import { useMemo } from 'react';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { SegmentsTrack } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/SegmentsTrack';
import { DEFAULT_PREMOUNT_DURATION } from '@goldcast/player-config/src/constants/Remotion.constants';
import { CompositeSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import VideoElementComponent from '../VideoElementComponent/VideoElementComponent';
import HidePreMountTrack from './HidePreMountTrack';
import { SegmentTransitionWrapper } from '../SegmentTransitionWrapper/SegmentTransitionWrapper';
import { createSegmentTransitionMap } from '../../libs/transitions/segmentTransition/utils';
import { PlayRangeCutFadeProvider } from '../SegmentComponent/PlayRangeCutFadeContext';
import { PlayRangesComponent } from '../SegmentComponent/PlayRangesComponent';
import PlayRangeComponent from '../SegmentComponent/PlayRangeComponent';
import { useRemotionUsage } from '../../MainPlayer/RemotionUsageContext/RemotionUsageContext';

const SegmentsTrackComponent: React.FC<{
  segments: SegmentsTrack['segments'];
  segmentTransitions?: SegmentsTrack['segmentTransitions'];
  canEditSpeakerLabel: boolean;
  canManuallyReposition?: boolean;
  preventOverlappingWithOtherCrops?: boolean;
}> = ({
  segments,
  segmentTransitions,
  canEditSpeakerLabel,
  canManuallyReposition,
  preventOverlappingWithOtherCrops
}) => {
  const currentFrame = useCurrentFrame();
  const { isPlayRangeMicroCutsEnabled } = useRemotionUsage();

  const transitionMap = useMemo(() => createSegmentTransitionMap(segmentTransitions), [segmentTransitions]);

  return segments.map(segment => {
    if (!segment) {
      return null;
    }

    const durationInFrames = segment.toFrame - segment.fromFrame;
    if (durationInFrames <= 0) {
      return null;
    }

    const transitions = transitionMap.get(segment.id);
    const outgoingTransition = transitions?.outgoing;
    const incomingTransition = transitions?.incoming;

    const renderSegment = () => {
      switch (segment.type) {
        case SegmentType.COMPOSITE: {
          const compositeSegment = segment as CompositeSegment;
          if (isPlayRangeMicroCutsEnabled) {
            return (
              <PlayRangesComponent
                segment={compositeSegment}
                canEditSpeakerLabel={canEditSpeakerLabel}
                canManuallyReposition={canManuallyReposition}
                preventOverlappingWithOtherCrops={preventOverlappingWithOtherCrops}
              />
            );
          }
          return (
            <PlayRangeCutFadeProvider playRanges={compositeSegment.playRanges}>
              <PlayRangeComponent
                segment={compositeSegment}
                canEditSpeakerLabel={canEditSpeakerLabel}
                canManuallyReposition={canManuallyReposition}
                preventOverlappingWithOtherCrops={preventOverlappingWithOtherCrops}
              />
            </PlayRangeCutFadeProvider>
          );
        }

        case SegmentType.VIDEO:
          return (
            <HidePreMountTrack isPreMounted={segment.fromFrame > currentFrame}>
              <VideoElementComponent element={segment.video} canEditSpeakerLabel={canEditSpeakerLabel} />
            </HidePreMountTrack>
          );
        default:
          return null;
      }
    };

    const segmentContent = renderSegment();
    if (!segmentContent) {
      return null;
    }

    let wrappedContent = segmentContent;

    if (incomingTransition) {
      wrappedContent = (
        <SegmentTransitionWrapper
          segmentId={segment.id}
          segmentDurationInFrames={durationInFrames}
          side="in"
          incomingTransitionForVolume={incomingTransition ?? null}
          outgoingTransitionForVolume={outgoingTransition ?? null}
        >
          {wrappedContent}
        </SegmentTransitionWrapper>
      );
    }

    if (outgoingTransition) {
      wrappedContent = (
        <SegmentTransitionWrapper
          segmentId={segment.id}
          segmentDurationInFrames={durationInFrames}
          side="out"
          incomingTransitionForVolume={incomingTransition ?? null}
          outgoingTransitionForVolume={outgoingTransition ?? null}
        >
          {wrappedContent}
        </SegmentTransitionWrapper>
      );
    }

    return (
      <Sequence
        from={segment.fromFrame}
        durationInFrames={durationInFrames}
        key={segment.id}
        premountFor={DEFAULT_PREMOUNT_DURATION}
      >
        {wrappedContent}
      </Sequence>
    );
  });
};

export default SegmentsTrackComponent;
